import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/invoice.mutation"

import type {
  ConfirmInvoiceAs400ManualReconciliationMutation,
  ConfirmInvoiceAs400ManualReconciliationMutationVariables,
  InvoiceAs400ManualReconciliationInput,
} from "@/graphql/codegen/graphql"
import { ConfirmInvoiceAs400ManualReconciliationDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseConfirmAs400ManualReconciliationReturn {
  confirmAs400ManualReconciliation: (input: InvoiceAs400ManualReconciliationInput) => Promise<boolean>
  isConfirming: boolean
}

export const useConfirmAs400ManualReconciliation = (): UseConfirmAs400ManualReconciliationReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    ConfirmInvoiceAs400ManualReconciliationMutation,
    ConfirmInvoiceAs400ManualReconciliationMutationVariables
  >(ConfirmInvoiceAs400ManualReconciliationDocument)

  const confirmAs400ManualReconciliation = useCallback(
    async (input: InvoiceAs400ManualReconciliationInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.invoice.confirmAs400ManualReconciliation

      if (output?.__typename === "InvoiceAs400ManualReconciliationSuccess" && !error) {
        notify({ type: NotificationType.Success, message: "AS400 sync exception marked as resolved" })
        return true
      }

      const failureMessage =
        output?.__typename === "InvoiceAs400ManualReconciliationFailure" ? output.error.message : undefined
      notify({
        type: NotificationType.Error,
        message: error?.message ?? failureMessage ?? "Failed to mark the AS400 sync exception as resolved",
      })
      return false
    },
    [executeMutation, notify]
  )

  return { confirmAs400ManualReconciliation, isConfirming: fetching }
}
